import React, { useMemo } from 'react';
import { Card, Descriptions, Flex, Typography, theme } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { CompanyEntry, Phone, PhonesMap } from '../api';

const { Text } = Typography;

/** Normalise phones from either array or keyed-object format into a Phone[]. */
function normalizePhonesArray(phones: Phone[] | PhonesMap): Phone[] {
  if (Array.isArray(phones)) return phones;
  return Object.values(phones);
}

interface CompanyDetailsProps {
  company: CompanyEntry;
}

/** Displays company address and contact information via Descriptions. */
export function CompanyDetails({ company }: CompanyDetailsProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('knowledge-base-module');
  const validPhones = normalizePhonesArray(company.phones).map((p) => p.value.trim()).filter(Boolean);
  const hasFax = company.fax?.trim();
  const hasEmails = company.emails?.trim();
  const addressParts = [company.street, [company.zip, company.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const hasState = company.state?.trim();

  const items = useMemo(() => {
    const result: Array<{ key: string; label: React.ReactNode; children: React.ReactNode }> = [
      {
        key: 'name',
        label: t('company.name'),
        children: <Text strong>{company.name}</Text>,
      },
    ];

    if (addressParts) {
      result.push({
        key: 'address',
        label: t('company.address'),
        children: addressParts,
      });
    }

    if (hasState) {
      result.push({
        key: 'state',
        label: t('company.state'),
        children: hasState,
      });
    }

    result.push({
      key: 'country',
      label: t('company.country'),
      children: company.country,
    });

    if (hasEmails) {
      result.push({
        key: 'email',
        label: t('company.email'),
        children: (
          <a href={`mailto:${hasEmails}`}>{hasEmails}</a>
        ),
      });
    }

    if (validPhones.length > 0) {
      result.push({
        key: 'phone',
        label: t('company.phone'),
        children: validPhones.join(', '),
      });
    }

    if (hasFax) {
      result.push({ key: 'fax', label: t('company.fax'), children: hasFax });
    }

    return result;
  }, [company, validPhones, hasFax, hasEmails, addressParts, hasState, t]);

  return (
    <Card
      title={
        <Flex align="center" gap={token.marginXS}>
          <EnvironmentOutlined style={{ color: token.colorPrimary }} />
          <span>{t('company.title')}</span>
        </Flex>
      }
      style={{ height: '100%' }}
    >
      <Descriptions column={1} colon={false} size="small" items={items} />
    </Card>
  );
}

export default CompanyDetails;
